import { useCallback, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import type { LastEndedCallInfo } from "../hooks/useATClient";
import { cn } from "../lib/cn";

// ─── Tag colour mapping ───────────────────────────────────────────────────────

const TAG_COLORS: Record<string, { pill: string; pillSelected: string }> = {
  blue: {
    pill: "bg-blue-50 text-blue-600 border-blue-200",
    pillSelected: "bg-blue-500 text-white border-blue-500",
  },
  green: {
    pill: "bg-green-50 text-green-600 border-green-200",
    pillSelected: "bg-green-500 text-white border-green-500",
  },
  purple: {
    pill: "bg-purple-50 text-purple-600 border-purple-200",
    pillSelected: "bg-purple-500 text-white border-purple-500",
  },
  orange: {
    pill: "bg-orange-50 text-orange-600 border-orange-200",
    pillSelected: "bg-orange-500 text-white border-orange-500",
  },
  pink: {
    pill: "bg-pink-50 text-pink-600 border-pink-200",
    pillSelected: "bg-pink-500 text-white border-pink-500",
  },
  teal: {
    pill: "bg-teal-50 text-teal-600 border-teal-200",
    pillSelected: "bg-teal-500 text-white border-teal-500",
  },
  red: {
    pill: "bg-red-50 text-red-600 border-red-200",
    pillSelected: "bg-red-500 text-white border-red-500",
  },
  amber: {
    pill: "bg-amber-50 text-amber-600 border-amber-200",
    pillSelected: "bg-amber-500 text-white border-amber-500",
  },
};

function tagColorClass(color: string, selected: boolean): string {
  return (TAG_COLORS[color] ?? TAG_COLORS["blue"]!)[selected ? "pillSelected" : "pill"];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(answeredAt: string | null, endedAt: string): string {
  if (!answeredAt) return "Not answered";
  const secs = Math.floor((new Date(endedAt).getTime() - new Date(answeredAt).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
}

function formatCallTime(endedAt: string): string {
  return new Date(endedAt).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface AccountTag {
  id: string;
  name: string;
  color: string;
}

interface DispositionCode {
  id: string;
  name: string;
  teamType: string;
  isDefault: boolean;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PostCallScreenProps {
  call: LastEndedCallInfo;
  accessToken: string;
  apiUrl: string;
  wrapupTimeSeconds: number;
  onDone: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PostCallScreen({
  call,
  accessToken,
  apiUrl,
  wrapupTimeSeconds,
  onDone,
}: PostCallScreenProps) {
  // ── ACW timer ───────────────────────────────────────────────────────────────
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const mountTimeRef = useRef(Date.now());

  // ── Fetch state ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [accountTags, setAccountTags] = useState<AccountTag[]>([]);
  const [dispositionCodes, setDispositionCodes] = useState<DispositionCode[]>([]);
  const [resolvedContactId, setResolvedContactId] = useState<string | null>(call.contactId);

  // ── Form state ──────────────────────────────────────────────────────────────
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedDispositionId, setSelectedDispositionId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Track whether the user has manually edited tags or notes
  const hasUserEditedTags = useRef(false);
  const hasUserEditedNotes = useRef(false);

  // Store contact tag names to seed selectedTagIds once accountTags loads
  const pendingContactTagNames = useRef<string[]>(call.contactTags ?? []);
  const tagsSeededRef = useRef(false);

  // ── ACW interval ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - mountTimeRef.current) / 1000));
    }, 1_000);
    return () => clearInterval(id);
  }, []);

  // ── handleDone (stable ref so auto-done timeout can call it) ─────────────────
  const handleDone = useCallback(() => {
    if (saving) return;
    setSaving(true);

    const elapsed = Math.floor((Date.now() - mountTimeRef.current) / 1000);
    const selectedTagNames = accountTags
      .filter((t) => selectedTagIds.includes(t.id))
      .map((t) => t.name);

    // Fire-and-forget patch calls
    void Promise.all([
      fetch(`${apiUrl}/calls/me/wrap-up`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          callId: call.callId ?? undefined,
          dispositionCodeId: selectedDispositionId ?? undefined,
          contactId: resolvedContactId ?? undefined,
          tags: selectedTagNames,
          notes,
          postCallTime: new Date().toISOString(),
          afterCallWorkSeconds: elapsed,
        }),
      }).catch(() => {}),
      resolvedContactId
        ? fetch(`${apiUrl}/contacts/${resolvedContactId}`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ tags: selectedTagNames, notes }),
          }).catch(() => {})
        : Promise.resolve(),
    ]);

    onDone();
  }, [
    saving,
    accountTags,
    selectedTagIds,
    selectedDispositionId,
    resolvedContactId,
    notes,
    apiUrl,
    accessToken,
    onDone,
  ]);

  // ── Auto-done after wrapupTimeSeconds ────────────────────────────────────────
  useEffect(() => {
    const id = setTimeout(handleDone, wrapupTimeSeconds * 1000);
    return () => clearTimeout(id);
  }, []);

  // ── On-mount data fetches ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const tagsPromise = fetch(`${apiUrl}/tags`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ tags: AccountTag[] }>) : { tags: [] }))
      .catch(() => ({ tags: [] as AccountTag[] }));

    const dispositionPromise = fetch(`${apiUrl}/disposition-codes`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ codes: DispositionCode[] }>) : { codes: [] }))
      .catch(() => ({ codes: [] as DispositionCode[] }));

    const contactPromise: Promise<{
      id: string;
      name: string | null;
      notes: string | null;
      tags: string[];
    } | null> = call.contactId
      ? fetch(`${apiUrl}/contacts/${call.contactId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
          .then((r) =>
            r.ok
              ? (r.json() as Promise<{
                  contact: {
                    id: string;
                    first_name: string | null;
                    last_name: string | null;
                    notes: string | null;
                    tags: string[];
                  };
                }>)
              : null,
          )
          .then((d) =>
            d?.contact
              ? {
                  ...d.contact,
                  name:
                    [d.contact.first_name, d.contact.last_name].filter(Boolean).join(" ") || null,
                }
              : null,
          )
          .catch(() => null)
      : call.remoteNumber
        ? fetch(`${apiUrl}/contacts?search=${encodeURIComponent(call.remoteNumber)}&limit=1`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
            .then((r) =>
              r.ok
                ? (r.json() as Promise<{
                    contacts: Array<{
                      id: string;
                      first_name: string | null;
                      last_name: string | null;
                      notes: string | null;
                      tags: string[];
                    }>;
                  }>)
                : null,
            )
            .then((d) => {
              const c = d?.contacts[0];
              return c
                ? { ...c, name: [c.first_name, c.last_name].filter(Boolean).join(" ") || null }
                : null;
            })
            .catch(() => null)
        : Promise.resolve(null);

    void Promise.all([tagsPromise, dispositionPromise, contactPromise]).then(
      ([tagsData, dispositionData, contactData]) => {
        if (cancelled) return;

        setAccountTags(tagsData.tags ?? []);
        setDispositionCodes(dispositionData.codes ?? []);

        if (contactData) {
          if (!call.contactId) {
            setResolvedContactId(contactData.id);
          }
          if (contactData.name) {
            setResolvedContactName(contactData.name);
          }
          if (!hasUserEditedNotes.current && contactData.notes) {
            setNotes(contactData.notes);
          }
          if (!hasUserEditedTags.current && contactData.tags?.length) {
            pendingContactTagNames.current = contactData.tags;
          }
        }

        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [apiUrl, accessToken, call.contactId, call.remoteNumber]);

  // ── Seed selectedTagIds once accountTags and contact tags are both known ──────
  useEffect(() => {
    if (tagsSeededRef.current) return;
    if (loading) return;
    if (accountTags.length === 0) return;
    if (hasUserEditedTags.current) return;

    tagsSeededRef.current = true;
    const names = pendingContactTagNames.current;
    if (names.length === 0) return;
    const matchedIds = accountTags.filter((t) => names.includes(t.name)).map((t) => t.id);
    if (matchedIds.length > 0) {
      setSelectedTagIds(matchedIds);
    }
  }, [loading, accountTags]);

  // ── Interaction handlers ─────────────────────────────────────────────────────
  const toggleTag = (id: string) => {
    hasUserEditedTags.current = true;
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };

  const toggleDisposition = (id: string) => {
    setSelectedDispositionId((prev) => (prev === id ? null : id));
  };

  const handleNotesChange = (value: string) => {
    hasUserEditedNotes.current = true;
    setNotes(value);
  };

  // ── Resolved display name — starts from what we know at call-end, updated
  //    once the contact fetch completes (covers outbound calls where contactName
  //    isn't populated until we look the number up).
  const [resolvedContactName, setResolvedContactName] = useState<string | null>(call.contactName);
  const duration = formatDuration(call.answeredAt, call.endedAt);
  const callTime = formatCallTime(call.endedAt);

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-background">
      {/* ── Top centred hero — mirrors ActiveCallScreen top area ─────────────── */}
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-8">
        {/* Timer pill — same design as in-call timer (primary bg) */}
        <div className="flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold shadow-sm text-primary-foreground">
          <Clock className="h-4 w-4" />
          <span>{formatElapsed(elapsedSeconds)}</span>
        </div>

        {/* Contact name or number — same size/weight as ActiveCallScreen */}
        <p className="text-2xl font-semibold tracking-tight text-foreground">
          {resolvedContactName ?? call.remoteNumber}
        </p>

        {/* Direction · Duration · Time */}
        <p className="text-xs text-muted-foreground">
          {duration} · {callTime}
        </p>
      </div>

      {/* ── Scrollable content ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="space-y-5">
          {loading ? (
            /* ── Loading skeleton ─────────────────────────────────────────── */
            <div className="space-y-3">
              <div className="h-8 animate-pulse rounded-xl bg-muted" />
              <div className="h-8 animate-pulse rounded-xl bg-muted" />
              <div className="h-8 animate-pulse rounded-xl bg-muted" />
            </div>
          ) : (
            <>
              {/* ── Disposition ─────────────────────────────────────────── */}
              {dispositionCodes.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Disposition
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {dispositionCodes.map((code) => {
                      const selected = selectedDispositionId === code.id;
                      return (
                        <button
                          key={code.id}
                          onClick={() => toggleDisposition(code.id)}
                          className={cn(
                            "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-card text-foreground hover:bg-muted/50",
                          )}
                        >
                          {code.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Tags ────────────────────────────────────────────────── */}
              {accountTags.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Tags
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {accountTags.map((tag) => {
                      const selected = selectedTagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag.id)}
                          className={cn(
                            "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                            tagColorClass(tag.color, selected),
                          )}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Notes ───────────────────────────────────────────────── */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Notes
                </p>
                <textarea
                  value={notes}
                  onChange={(e) => handleNotesChange(e.target.value)}
                  placeholder="Add call notes..."
                  className="min-h-[80px] w-full resize-none rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Sticky Done button ──────────────────────────────────────────────── */}
      <div className="border-t border-border bg-background px-4 py-4">
        <button
          onClick={handleDone}
          disabled={saving}
          className={cn(
            "w-full rounded-xl py-3 text-sm font-semibold text-primary-foreground transition-opacity",
            saving ? "cursor-not-allowed bg-primary opacity-70" : "bg-primary",
          )}
        >
          {saving ? "Saving..." : "Done"}
        </button>
      </div>
    </div>
  );
}
